import React, { useState, useEffect } from 'react';
import { api } from '@/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertTriangle, Settings, ChevronRight } from 'lucide-react';

export default function ContractRulesDispute({ projectId, settings }) {
  const [profiles, setProfiles] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedFamily, setSelectedFamily] = useState(settings?.contract_family || 'NEC');

  useEffect(() => {
    (async () => {
      try {
        const res = await api.disputes.getProfiles();
        setProfiles(res.data || {});
      } catch { /* silent */ } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-slate-300 border-t-slate-800 rounded-full" /></div>;

  const families = Object.keys(profiles);
  const active = profiles[selectedFamily] || {};
  const activeFamily = settings?.contract_family || 'NEC';

  return (
    <div className="space-y-4" data-testid="dispute-contract-rules">
      <h2 className="text-lg font-barlow font-bold uppercase tracking-tight">Templates & Contract Rules</h2>

      {/* Warning */}
      <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
        <p className="text-xs text-amber-800">Platform defaults are starting assumptions only. Project-specific contract documents, particular conditions, special conditions, and amendments override all defaults.</p>
      </div>

      {/* Active Profile */}
      <Card className="border-green-200">
        <CardContent className="p-4">
          <div className="flex items-center gap-2">
            <Settings className="h-4 w-4 text-green-600" />
            <span className="text-sm font-bold text-green-800">Active Profile: {activeFamily}</span>
            <Badge className="bg-green-100 text-green-700 text-[10px]">Current</Badge>
          </div>
        </CardContent>
      </Card>

      {/* Profile Tabs */}
      <Tabs value={selectedFamily} onValueChange={setSelectedFamily}>
        <TabsList className="bg-white border">
          {families.map(f => (
            <TabsTrigger key={f} value={f} className="text-xs">{f}</TabsTrigger>
          ))}
        </TabsList>

        {families.map(f => {
          const p = profiles[f] || {};
          return (
            <TabsContent key={f} value={f} className="space-y-4">
              {/* Terminology */}
              <Card>
                <CardContent className="p-4">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Terminology</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {Object.entries(p.terminology || {}).map(([key, val]) => (
                      <div key={key} className="p-2 bg-slate-50 rounded">
                        <p className="text-[10px] text-slate-400 uppercase">{key.replace(/_/g, ' ')}</p>
                        <p className="text-sm font-medium text-slate-800">{val}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Stages */}
              <Card>
                <CardContent className="p-4">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Dispute Stages</h3>
                  <div className="space-y-1">
                    {(p.stages || []).map((s, i) => (
                      <div key={s.code} className="flex items-center gap-2 py-1.5 border-b last:border-b-0">
                        <span className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-bold text-slate-500">{s.seq}</span>
                        <span className="text-sm text-slate-700 flex-1">{s.label}</span>
                        {s.optional && <Badge variant="outline" className="text-[8px]">Optional</Badge>}
                        <ChevronRight className="h-3 w-3 text-slate-300" />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Notice Types */}
              <Card>
                <CardContent className="p-4">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Notice Types</h3>
                  <div className="flex flex-wrap gap-1.5">
                    {(p.notice_types || []).map(nt => (
                      <Badge key={nt} variant="outline" className="text-xs capitalize">{nt.replace(/_/g, ' ')}</Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Procedural Steps */}
              <Card>
                <CardContent className="p-4">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Default Procedural Steps</h3>
                  <div className="space-y-1.5">
                    {(p.procedural_steps || []).map(ps => (
                      <div key={ps.code} className="flex items-center gap-3 py-1.5 border-b last:border-b-0">
                        <span className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-bold text-slate-500">{ps.seq}</span>
                        <span className="text-sm text-slate-700 flex-1">{ps.label}</span>
                        {ps.mandatory && <Badge className="bg-slate-900 text-white text-[8px]">Mandatory</Badge>}
                        {ps.days > 0 && <span className="text-xs text-slate-400">{ps.days}d</span>}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Forum Routes */}
              <Card>
                <CardContent className="p-4">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Forum Routes</h3>
                  <div className="flex gap-2">
                    {(p.forum_routes || []).map(r => (
                      <Badge key={r} className="bg-slate-100 text-slate-700 text-xs capitalize">{r.replace(/_/g, ' ')}</Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Deadline Defaults */}
              <Card>
                <CardContent className="p-4">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Deadline Defaults</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(p.deadline_defaults || {}).map(([key, val]) => (
                      <div key={key} className="p-2 bg-slate-50 rounded">
                        <p className="text-[10px] text-slate-400 uppercase">{key.replace(/_/g, ' ')}</p>
                        <p className="text-sm font-medium text-slate-800">{val}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
}
